import { HTTP_INTERCEPTORS, provideHttpClient, withXhr } from '@angular/common/http';
import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideIcons } from 'app/core/icons/icons.provider';
import { IconsMockApi } from '@shared/services/icons/api';
import { NavigationMockApi } from '@shared/services/navigation/api';
import { UserMockApi } from '@shared/services/user/api';
import { TokenInterceptor } from '@shared/interceptors/token.interceptor';
import { PublicTokenInterceptor } from '@shared/interceptors/public-token.interceptor';
import { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';

// AG Grid 33+ defaults to its new Theming API. This application still uses
// the Alpine CSS theme and its custom CSS variables, so retain legacy mode
// until those styles are deliberately migrated to the new API.
provideGlobalGridOptions({
  theme: 'legacy',
  // Keep legacy application grids non-resizable unless a grid or column
  // explicitly opts in (for example the import and efficiency-report grids).
  defaultColDef: {
    resizable: false,
  },
  // AG Grid 32+ renders row-selection checkboxes in a generated column.
  // Match the 50px selector columns used by the legacy column definitions;
  // grids with a deliberately different selector width override this value.
  selectionColumnDef: {
    width: 50,
    resizable: false,
    suppressSizeToFit: false,
    headerClass: 'left-aligned',
    cellStyle: {
      'justify-content': 'flex-start',
    },
  },
});
ModuleRegistry.registerModules([AllCommunityModule]);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    provideHttpClient(withXhr()),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    // Angular Material 22 displays a check indicator in single-select toggle
    // groups by default. Keep the application's pre-upgrade toggle appearance.
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        hideSingleSelectionIndicator: true,
      },
    },

    // Fuse
    provideIcons(),
    provideFuse({
      mockApi: {
        delay: 0,
        services: [IconsMockApi, NavigationMockApi, UserMockApi],
      },
      fuse: {
        layout: 'classy',
        scheme: 'light',
        screens: {
          xsm: '485px',
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [{ id: 'theme-default', name: 'Optimum Grading' }],
      },
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true, // Set multi to true to allow multiple interceptors
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: PublicTokenInterceptor,
    //   multi: true, // Set multi to true to allow multiple interceptors
    // },
  ],
};
